import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Bell, Mail, Save, TestTube, Clock } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface Organization {
  id: string;
  name: string;
}

interface NotificationsTabProps {
  organization: Organization;
  onSave: (data: any) => void;
}

export function NotificationsTab({ organization, onSave }: NotificationsTabProps) {
  const { toast } = useToast()
  
  const [notifications, setNotifications] = useState({
    analysis_complete: {
      enabled: true,
      recipients: ['creator'],
      email_template: 'standard'
    },
    job_failed: {
      enabled: true,
      recipients: ['creator', 'admin'],
      email_template: 'standard'
    },
    new_findings: {
      enabled: false,
      recipients: ['creator'],
      email_template: 'standard'
    },
    daily_digest: {
      enabled: true,
      time: '07:00',
      recipients: ['admin'],
      email_template: 'digest'
    },
    weekly_digest: {
      enabled: false,
      time: '09:00',
      day: 'monday',
      recipients: ['admin'],
      email_template: 'digest'
    },
    low_balance: {
      enabled: true,
      threshold: 100,
      recipients: ['admin'],
      email_template: 'alert'
    }
  })

  const [quietHours, setQuietHours] = useState({
    enabled: true,
    start: '22:00',
    end: '07:00',
    timezone: 'Europe/Stockholm'
  })

  const handleSave = () => {
    onSave({ notifications, quietHours })
  }

  const sendTestEmail = async (type: string) => {
    toast({
      title: "Testmail skickat",
      description: `Testmail för ${type} har skickats till alla mottagare.`,
    })
  }

  const recipientOptions = [
    { value: 'creator', label: 'Skapare av ärende' },
    { value: 'admin', label: 'Administratörer' },
    { value: 'all', label: 'Alla användare' },
    { value: 'custom', label: 'Anpassad lista' }
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            E-postnotifieringar
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Analysis Complete */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Analys klar</Label>
                <p className="text-sm text-muted-foreground">När en AI-analys är slutförd</p>
              </div>
              <div className="flex items-center gap-4">
                <Switch
                  checked={notifications.analysis_complete.enabled}
                  onCheckedChange={(checked) => 
                    setNotifications(prev => ({
                      ...prev,
                      analysis_complete: { ...prev.analysis_complete, enabled: checked }
                    }))
                  }
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sendTestEmail('analys klar')}
                  className="gap-2"
                >
                  <TestTube className="h-4 w-4" />
                  Test
                </Button>
              </div>
            </div>
            {notifications.analysis_complete.enabled && (
              <div className="ml-4 p-3 bg-muted/50 rounded-lg">
                <Label className="text-sm">Mottagare</Label>
                <Select 
                  value={notifications.analysis_complete.recipients[0]} 
                  onValueChange={(value) => 
                    setNotifications(prev => ({
                      ...prev,
                      analysis_complete: { ...prev.analysis_complete, recipients: [value] }
                    }))
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {recipientOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Job Failed */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Jobb misslyckat</Label>
                <p className="text-sm text-muted-foreground">När en AI-analys misslyckas</p>
              </div>
              <div className="flex items-center gap-4">
                <Switch
                  checked={notifications.job_failed.enabled}
                  onCheckedChange={(checked) => 
                    setNotifications(prev => ({
                      ...prev,
                      job_failed: { ...prev.job_failed, enabled: checked }
                    }))
                  }
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sendTestEmail('jobb misslyckat')}
                  className="gap-2"
                >
                  <TestTube className="h-4 w-4" />
                  Test
                </Button>
              </div>
            </div>
          </div>

          {/* New Findings */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Nya findings/åtgärder</Label>
                <p className="text-sm text-muted-foreground">När nya fynd eller åtgärder skapas</p>
              </div>
              <div className="flex items-center gap-4">
                <Switch
                  checked={notifications.new_findings.enabled}
                  onCheckedChange={(checked) => 
                    setNotifications(prev => ({
                      ...prev,
                      new_findings: { ...prev.new_findings, enabled: checked }
                    }))
                  }
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sendTestEmail('nya findings')}
                  className="gap-2"
                >
                  <TestTube className="h-4 w-4" />
                  Test
                </Button>
              </div>
            </div>
          </div>

          {/* Daily Digest */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Daglig digest</Label>
                <p className="text-sm text-muted-foreground">Sammanfattning av dagens aktivitet</p>
              </div>
              <div className="flex items-center gap-4">
                <Switch
                  checked={notifications.daily_digest.enabled}
                  onCheckedChange={(checked) => 
                    setNotifications(prev => ({
                      ...prev,
                      daily_digest: { ...prev.daily_digest, enabled: checked }
                    }))
                  }
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sendTestEmail('daglig digest')}
                  className="gap-2"
                >
                  <TestTube className="h-4 w-4" />
                  Test
                </Button>
              </div>
            </div>
            {notifications.daily_digest.enabled && (
              <div className="ml-4 p-3 bg-muted/50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Tid</Label>
                    <Input
                      type="time"
                      value={notifications.daily_digest.time}
                      onChange={(e) => 
                        setNotifications(prev => ({
                          ...prev,
                          daily_digest: { ...prev.daily_digest, time: e.target.value }
                        }))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Mottagare</Label>
                    <Select 
                      value={notifications.daily_digest.recipients[0]} 
                      onValueChange={(value) => 
                        setNotifications(prev => ({
                          ...prev,
                          daily_digest: { ...prev.daily_digest, recipients: [value] }
                        }))
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {recipientOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Low Balance */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Lågt saldo</Label>
                <p className="text-sm text-muted-foreground">När kontosaldot når tröskelvärdet</p>
              </div>
              <div className="flex items-center gap-4">
                <Switch
                  checked={notifications.low_balance.enabled}
                  onCheckedChange={(checked) => 
                    setNotifications(prev => ({
                      ...prev,
                      low_balance: { ...prev.low_balance, enabled: checked }
                    }))
                  }
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sendTestEmail('lågt saldo')}
                  className="gap-2"
                >
                  <TestTube className="h-4 w-4" />
                  Test
                </Button>
              </div>
            </div>
            {notifications.low_balance.enabled && (
              <div className="ml-4 p-3 bg-muted/50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Tröskel (SEK)</Label>
                    <Input
                      type="number"
                      value={notifications.low_balance.threshold}
                      onChange={(e) => 
                        setNotifications(prev => ({
                          ...prev,
                          low_balance: { ...prev.low_balance, threshold: parseInt(e.target.value) }
                        }))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Mottagare</Label>
                    <Select 
                      value={notifications.low_balance.recipients[0]} 
                      onValueChange={(value) => 
                        setNotifications(prev => ({
                          ...prev,
                          low_balance: { ...prev.low_balance, recipients: [value] }
                        }))
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {recipientOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Tysta tider
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Aktivera tysta tider</Label>
              <p className="text-sm text-muted-foreground">Inga notifieringar skickas under denna period</p>
            </div>
            <Switch
              checked={quietHours.enabled}
              onCheckedChange={(checked) => setQuietHours(prev => ({ ...prev, enabled: checked }))}
            />
          </div>
          
          {quietHours.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Från</Label>
                <Input
                  type="time"
                  value={quietHours.start}
                  onChange={(e) => setQuietHours(prev => ({ ...prev, start: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Till</Label>
                <Input
                  type="time"
                  value={quietHours.end}
                  onChange={(e) => setQuietHours(prev => ({ ...prev, end: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Save className="h-4 w-4" />
          Spara notifieringsinställningar
        </Button>
      </div>
    </div>
  )
}